import { clsx } from '@alexis/helpers/clsx';
import CheckboxItem from '@pages/FlightSearchResult/components/CheckboxItem';
import { FlightFilterCategories } from '@pages/FlightSearchResult/components/FlightFilters/type';
import { removeSearchParams } from '@pages/FlightSearchResult/components/SortAndFilterBar/helper';
import { useFlightPLS } from '@pages/FlightSearchResult/contexts/FlightPerLegSearchContext/FlightPerLegSearchContext';
import { useFlightSearchResults } from '@pages/FlightSearchResult/contexts/FlightSearchResultsContext';
import useFlightSearchFilters from '@pages/FlightSearchResult/hooks/useFlightSearchFilters';
import useFlightSearchPageParams from '@pages/FlightSearchResult/hooks/useFlightSearchPageParams';
import { GenzoTrackApplyFilterActionEventType } from '@pages/FlightSearchResult/types';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import Switch from '@components/Switch';

import { convertFilterStateToDataForTracking, mergeDuplicateNameAirlines } from '@helpers/flight';
import { convertFlightSearchResultFarePriceToPrice } from '@helpers/price';

import useFlightSearchResultsDatadog from '@hooks/useFlightSearchResultsDatadog';

import { getIsRtlState, getPriceOptionsState, getTranslationsState } from '@redux/selectors';

import { FlightSearchResultSearchParams } from '@wegoTypes/flights/flightSearchParams';

import styles from '../FlightFilterCategoryContent.module.scss';

const DEFAULT_NUMBER_OF_OPTIONS_SHOWED = 8;

type AirlinesFilterProps = {
  genzoTrackApplyFilterActionEvent: GenzoTrackApplyFilterActionEventType;
  genzoTrackApplyFilterSearchView: () => void;
};

const AirlinesFilter: React.FC<AirlinesFilterProps> = ({
  genzoTrackApplyFilterActionEvent,
  genzoTrackApplyFilterSearchView,
}) => {
  const { passengerCount, isMultiCity } = useFlightSearchPageParams();

  const [searchParams, setSearchParams] = useSearchParams();

  const {
    tripsFilter,
    airlineCodesFilter,
    allianceCodesFilter,
    toggleSingleFilter,
    toggleMultiCitySingleFilter,
    selectAllFilterOptions,
    clearFilter,
    activeLeg,
  } = useFlightSearchFilters();
  const { isPerLegSearch } = useFlightPLS();

  const { multiCityActiveLeg, airlineFilterOptions, allianceFilterOptions } =
    useFlightSearchResults();

  const activeLegAirlineFilterOptions = isMultiCity
    ? mergeDuplicateNameAirlines(
        multiCityActiveLeg.leg?.airlines
          .map((airline, index) => ({
            id: index,
            name: airline?.code,
            price: airline?.price,
            airline: airline?.airline,
          }))
          .sort((a, b) => a.airline?.name?.localeCompare(b.airline?.name)) || [],
      )
    : mergeDuplicateNameAirlines(airlineFilterOptions);

  const [airlineAllianceViewMode, setAirlineAllianceViewMode] = useState<'airline' | 'alliance'>(
    'airline',
  );
  const [isAirlinesOptionsExpanded, setIsAirlinesOptionsExpanded] = useState(
    activeLegAirlineFilterOptions.length > DEFAULT_NUMBER_OF_OPTIONS_SHOWED,
  );

  const translations = useSelector(getTranslationsState);
  const isRtl = useSelector(getIsRtlState);
  const priceOptions = useSelector(getPriceOptionsState);

  const { exchangeRate } = useFlightSearchResults();

  const { recordChangeFilterAction } = useFlightSearchResultsDatadog();

  const selectedAirlineForActiveLeg =
    airlineCodesFilter.find((item) => item.leg === activeLeg)?.values || [];

  const selectedAllianceForActiveLeg =
    allianceCodesFilter.find((item) => item.leg === activeLeg)?.values || [];

  const handleTripOptionChanged = (option: string) => {
    recordChangeFilterAction();

    const newFilter = toggleSingleFilter({
      searchParamKey: FlightSearchResultSearchParams.TripOptions,
      value: option,
    });

    genzoTrackApplyFilterActionEvent('filter', 'applied', {
      complete_trip_same_airline: !newFilter.includes(option) ? 1 : 0,
    });
    genzoTrackApplyFilterSearchView();
  };

  const handleAllianceClick = (code: string) => {
    recordChangeFilterAction();

    const newFilter = toggleMultiCitySingleFilter({
      activeLeg: isPerLegSearch ? activeLeg : 0,
      value: code,
      searchParamKey: FlightSearchResultSearchParams.Alliances,
    });

    genzoTrackApplyFilterActionEvent('filter', 'applied', {
      alliances: convertFilterStateToDataForTracking(
        newFilter,
        false,
        true,
        isPerLegSearch,
        activeLeg,
      ),
    });

    genzoTrackApplyFilterSearchView();
  };

  const handleSelectAllAlliancesClick = () => {
    selectAllFilterOptions({
      searchParamKey: FlightSearchResultSearchParams.Alliances,
      filterOptions: allianceFilterOptions,
      activeLeg,
    });

    genzoTrackApplyFilterSearchView();
  };

  const handleClearAllAlliancesClick = () => {
    if (isPerLegSearch) {
      removeSearchParams(searchParams, FlightFilterCategories.Alliances, activeLeg);

      setSearchParams(searchParams, { replace: true });
    } else {
      clearFilter({ searchParamKey: FlightSearchResultSearchParams.Alliances });
      genzoTrackApplyFilterSearchView();
    }
  };

  const handleAirlineClick = (codes: string[]) => {
    recordChangeFilterAction();

    const newFilter = toggleMultiCitySingleFilter({
      activeLeg,
      value: codes,
      searchParamKey: FlightSearchResultSearchParams.Airlines,
    });

    genzoTrackApplyFilterActionEvent('filter', 'applied', {
      airlines: convertFilterStateToDataForTracking(
        newFilter,
        isMultiCity,
        true,
        isPerLegSearch,
        activeLeg,
      ),
    });

    genzoTrackApplyFilterSearchView();
  };

  const handleSelectAllAirlinesClick = () => {
    selectAllFilterOptions({
      searchParamKey: FlightSearchResultSearchParams.Airlines,
      filterOptions: activeLegAirlineFilterOptions,
      activeLeg,
    });

    genzoTrackApplyFilterSearchView();
  };

  const handleClearAllAirlinesClick = () => {
    if (isPerLegSearch) {
      removeSearchParams(searchParams, FlightFilterCategories.Airlines, activeLeg);

      setSearchParams(searchParams, { replace: true });
    } else {
      clearFilter({
        searchParamKey: FlightSearchResultSearchParams.Airlines,
        activeLeg,
      });

      genzoTrackApplyFilterSearchView();
    }
  };

  return (
    <div className={styles.categoryContent}>
      {!isMultiCity && !isPerLegSearch ? (
        <div
          data-testid='same-airline-switch-container'
          className={styles.itemBoolean}
          onClick={() => handleTripOptionChanged('same_airline')}
        >
          {translations.complete_trip_airline}

          <Switch isRtl={isRtl} checked={tripsFilter.includes('same_airline')} />
        </div>
      ) : null}

      {!isMultiCity && (
        <div className={styles.airlineAllianceSelector}>
          <span
            className={clsx(airlineAllianceViewMode === 'airline' && styles.selected)}
            onClick={() => setAirlineAllianceViewMode('airline')}
          >
            {translations.airline}
          </span>
          <i className={styles.divider}></i>
          <span
            className={clsx(airlineAllianceViewMode === 'alliance' && styles.selected)}
            onClick={() => setAirlineAllianceViewMode('alliance')}
          >
            {translations.alliance}
          </span>
        </div>
      )}

      {allianceFilterOptions.length > 0 &&
        !isMultiCity &&
        airlineAllianceViewMode === 'alliance' && (
          <>
            <div className={styles.alliances}>
              {allianceFilterOptions.map((alliance) => (
                <div
                  key={alliance.name}
                  data-testid={alliance.name}
                  className={clsx(
                    styles.alliance,
                    styles[alliance.name],
                    selectedAllianceForActiveLeg.includes(alliance.name) && styles.selected,
                  )}
                  onClick={() => handleAllianceClick(alliance.name)}
                >
                  <div className={clsx(styles.tooltip, styles.bottom)}>
                    {translations[alliance.name] || ''}
                  </div>
                </div>
              ))}
            </div>

            {allianceFilterOptions.length > 1 && (
              <div className={styles.selectAllContainer}>
                <span onClick={handleSelectAllAlliancesClick}>{translations.select_all}</span>
                <i className={styles.divider}></i>
                <span onClick={handleClearAllAlliancesClick}>{translations.clear}</span>
              </div>
            )}
          </>
        )}

      {(airlineAllianceViewMode === 'airline' || isMultiCity) && (
        <>
          {(isAirlinesOptionsExpanded
            ? activeLegAirlineFilterOptions
            : activeLegAirlineFilterOptions.slice(0, 8)
          ).map((airline) => (
            <CheckboxItem
              key={airline.name[0]}
              isChecked={airline.name.every((airlineName) =>
                selectedAirlineForActiveLeg.includes(airlineName),
              )}
              label={airline.airline?.name ?? ''}
              exchangeRate={exchangeRate}
              price={convertFlightSearchResultFarePriceToPrice(
                airline.price!,
                passengerCount,
                priceOptions.showTotalPrice,
                true,
              )}
              onCheckboxToggle={() => handleAirlineClick(airline.name)}
            />
          ))}

          {activeLegAirlineFilterOptions.length > DEFAULT_NUMBER_OF_OPTIONS_SHOWED ? (
            <div
              className={clsx(styles.showMore, isAirlinesOptionsExpanded && styles.expanded)}
              onClick={() => setIsAirlinesOptionsExpanded((prev) => !prev)}
            >
              {isAirlinesOptionsExpanded ? translations.show_less : translations.show_more}
              <i className={clsx(styles.chevron, styles.bottom)}></i>
            </div>
          ) : null}

          {activeLegAirlineFilterOptions.length > 1 ? (
            <div className={styles.selectAllContainer}>
              <span onClick={handleSelectAllAirlinesClick}>{translations.select_all}</span>
              <i className={styles.divider}></i>
              <span onClick={handleClearAllAirlinesClick}>{translations.clear}</span>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};

export default AirlinesFilter;
